import { useMemo } from "react";
import {
  AssessmentQuestionType,
  EngagementSurveyReportResponderFragment,
  EngagementSurveyReportRowFragment,
  EngagementSurveyRollupType,
} from "types/graphql-schema";

import StickyColumnShadow from "@apps/org-settings/components/competencies/sticky-column-shadow";
import { assessmentQuestionIcon } from "@apps/programs/components/compliance-program-form/assessment-group-sidebar/assessment-group-sidebar-question-constants";
import useLabel from "@apps/use-label/use-label";
import Avatar from "@components/avatar/avatar";
import Tooltip from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import { resultColWidth, stickyColWidth } from "../engagement-survey-report";
import {
  getNpsColorValue,
  getRangeColorValue,
  getResponderKey,
  isHighContrastBackground,
} from "./helpers";
import { EngagementSurveyReportTableType } from "./types";

const RollupHeading = ({
  responder,
}: {
  responder: EngagementSurveyReportResponderFragment;
}): string | JSX.Element => {
  const label = useLabel();
  switch (responder.rollupType) {
    case EngagementSurveyRollupType.Organization:
      return label("organization", { capitalize: true });

    case EngagementSurveyRollupType.Team:
      return assertNonNull(responder.team?.title);

    case EngagementSurveyRollupType.CareerTrack:
      return assertNonNull(responder.careerTrack?.title);

    case EngagementSurveyRollupType.Location:
      return assertNonNull(responder.location);

    case EngagementSurveyRollupType.IsManager:
      return `${responder.isManager ? "Managers" : "ICs"}`;

    case EngagementSurveyRollupType.Manager:
      return (
        <div className="flex items-center gap-2">
          <Avatar size="5" user={responder.manager} />
          {responder.manager?.name}
        </div>
      );
    case EngagementSurveyRollupType.Collapsed:
      return "Other";
  }
};

const EngagementSurveyReportTable = ({
  responders,
  questionRows,
}: {
  responders: EngagementSurveyReportResponderFragment[];
  questionRows: EngagementSurveyReportRowFragment[];
  type: EngagementSurveyReportTableType;
}) => {
  const visibleResponders = useMemo(
    () => responders.filter(({ isCollapsed }) => !isCollapsed),
    [responders],
  );

  return (
    <div className="relative overflow-x-auto border rounded-md w-fit max-w-full">
      <table className="text-sm table-fixed">
        <thead>
          <tr>
            <th
              className={classNames(
                stickyColWidth,
                "p-4 text-left border-b sticky left-0 bg-gray-50 z-10 align-top",
              )}
            >
              <div className="font-medium text-sm">Question</div>
              <StickyColumnShadow />
            </th>
            {visibleResponders.map((responder, columnIndex) => {
              return (
                <th
                  key={`${responder.rollupType}-${getResponderKey(responder)}`}
                  className={classNames(
                    resultColWidth,
                    columnIndex < visibleResponders.length - 1 && "border-r",
                    "p-4 border-b font-medium text-sm align-top bg-gray-50",
                  )}
                >
                  <div className="flex flex-col items-center gap-1">
                    <RollupHeading responder={responder} />
                    <div className="text-xs flex items-center gap-1">
                      <TopicflowIcon type="user" className="h-3 w-3" />
                      <div>{responder.responderCount}</div>
                    </div>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {questionRows.map((question, rowIndex) => {
            const QuestionIcon =
              question.question?.__typename === "RangeAssessmentQuestionNode"
                ? assessmentQuestionIcon[AssessmentQuestionType.Range]
                : assessmentQuestionIcon[AssessmentQuestionType.Nps];
            return (
              <tr key={question.question.id}>
                <td
                  className={classNames(
                    stickyColWidth,
                    rowIndex < questionRows.length - 1 && "border-b",
                    "p-4 sticky left-0 z-10 text-sm bg-white",
                  )}
                >
                  <div className="flex flex-col gap-1">
                    <div>{question.title || "Unnamed Section"}</div>
                    {question.question && (
                      <div className="text-xs text-gray-400 flex items-center gap-2">
                        <QuestionIcon className="h-4 w-4" />
                        {question.question?.__typename ===
                        "RangeAssessmentQuestionNode"
                          ? `Range (${question.question?.startValue}-${question.question?.endValue})`
                          : "eNPS"}
                      </div>
                    )}
                  </div>
                  <StickyColumnShadow />
                </td>

                {visibleResponders.map((responder, columnIndex) => {
                  const rollup = question.rollups.edges
                    .map((edge) => assertNonNull(edge?.node))
                    .find(
                      (rollup) =>
                        rollup.rollupType === responder.rollupType &&
                        getResponderKey(rollup) === getResponderKey(responder),
                    );

                  const value = rollup?.value ? rollup.value : 0;
                  const backgroundColor =
                    responder.responderCount === 0 || responder.isHidden
                      ? "#ffffff"
                      : question.question?.__typename ===
                          "RangeAssessmentQuestionNode"
                        ? getRangeColorValue(
                            (value / question.question?.endValue) * 100,
                          )
                        : getNpsColorValue(value);
                  return (
                    <td
                      key={`${responder.rollupType}-${getResponderKey(responder)}`}
                      className={classNames(
                        resultColWidth,
                        rowIndex < questionRows.length - 1 && "border-b",
                        columnIndex < visibleResponders.length - 1 &&
                          "border-r",
                        !isHighContrastBackground(backgroundColor) &&
                          "text-white",
                        "p-4 text-center align-middle",
                      )}
                      style={{ backgroundColor }}
                    >
                      {responder.responderCount === 0 ? (
                        <div className="text-sm">N/A</div>
                      ) : responder.isHidden ? (
                        <div className="flex flex-col items-center gap-2.5 text-gray-400">
                          <Tooltip text="To keep identities anonymous, we cannot display data when there is less than 3 people within a group.">
                            <span>
                              <TopicflowIcon type="info" className="h-4 w-4" />
                            </span>
                          </Tooltip>
                          <div className="text-sm">
                            Not enough responses to show results
                          </div>
                        </div>
                      ) : question.question?.__typename ===
                        "RangeAssessmentQuestionNode" ? (
                        <div className="text-sm font-semibold flex items-center justify-center gap-1">
                          {value}
                          <div className="opacity-50">
                            / {question.question?.endValue}
                          </div>
                        </div>
                      ) : (
                        <div className="text-sm font-semibold">{value}</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default EngagementSurveyReportTable;
